import { hideDialog, openDialog } from '../base/dialog/actions';
import { isMobileBrowser } from '../base/environment/utils';
import JitsiMeetJS, { JitsiConferenceEvents } from '../base/lib-jitsi-meet';
import { pinParticipant } from '../base/participants/actions';
import { getParticipantDisplayName, getPinnedParticipant, getVirtualScreenshareParticipantByOwnerId } from '../base/participants/functions';
import { toggleScreensharing } from '../base/tracks/actions';
import { getLocalDesktopTrack } from '../base/tracks/functions';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { isScreenVideoShared } from '../screen-share/functions';
import { CAPTURE_EVENTS, REMOTE_CONTROL_ACTIVE, SET_AGENT_STATE, SET_CHOSEN_DISPLAY, SET_CONTROLLED_PARTICIPANT, SET_CONTROLLER, SET_CONTROLLER_ENGAGED, SET_RECEIVER_ENABLED, SET_RECEIVER_TRANSPORT, SET_REQUESTED_PARTICIPANT } from './actionTypes';
import { connectToAgent, startAgentSession } from './agent';
import RemoteControlAuthorizationDialog from './components/web/RemoteControlAuthorizationDialog';
import { DISCO_REMOTE_CONTROL_FEATURE, EVENTS, PERMISSIONS_ACTIONS, REMOTE_CONTROL_MESSAGE_NAME, REQUESTS } from './constants';
import { findSharedDisplay, getSharedDisplaySize, isRemoteControlEnabled, isWholeScreenShared, sendRemoteControlEndpointMessage } from './functions';
import logger from './logger';
import { AgentStatus } from './types';
/**
 * Listeners.
 */
let permissionsReplyListener, receiverEndpointMessageListener, stopListener;
/**
 * Signals that the remote control authorization dialog should be displayed.
 *
 * @param {string} participantId - The id of the participant who is requesting
 * the authorization.
 * @returns {{
 *     type: OPEN_DIALOG,
 *     component: {RemoteControlAuthorizationDialog},
 *     componentProps: {
 *         participantId: {string}
 *      }
 * }}
 * @public
 */
export function openRemoteControlAuthorizationDialog(participantId) {
    return openDialog('RemoteControlAuthorizationDialog', RemoteControlAuthorizationDialog, { participantId });
}
/**
 * Sets the remote control active property.
 *
 * @param {boolean} active - The new value for the active property.
 * @returns {Function}
 */
export function setRemoteControlActive(active) {
    return (dispatch, getState) => {
        const state = getState();
        const { active: oldActive } = state['features/remote-control'];
        const { conference } = state['features/base/conference'];
        if (active !== oldActive) {
            dispatch({
                type: REMOTE_CONTROL_ACTIVE,
                active
            });
            conference?.setLocalParticipantProperty('remoteControlSessionStatus', active);
        }
    };
}
/**
 * Requests permissions from the remote control receiver side.
 *
 * @param {string} userId - The user id of the participant that will be
 * requested.
 * @returns {Function}
 */
export function requestRemoteControl(userId) {
    return (dispatch, getState) => {
        const state = getState();
        const enabled = isRemoteControlEnabled(state);
        if (!enabled) {
            return Promise.reject(new Error('Remote control is disabled!'));
        }
        dispatch(setRemoteControlActive(true));
        logger.log(`Requesting remote control permissions from: ${userId}`);
        const { conference } = state['features/base/conference'];
        permissionsReplyListener = (participant, event) => {
            dispatch(processPermissionRequestReply(participant.getId(), event));
        };
        conference?.on(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, permissionsReplyListener);
        dispatch({
            type: SET_REQUESTED_PARTICIPANT,
            requestedParticipant: userId
        });
        if (!sendRemoteControlEndpointMessage(conference, userId, {
            type: EVENTS.permissions,
            action: PERMISSIONS_ACTIONS.request
        })) {
            dispatch(clearRequest());
        }
    };
}
/**
 * Withdraws a request for control that has not been answered yet.
 *
 * Clearing it locally is not enough: the other person still has a dialog open
 * asking whether to allow something nobody is waiting for, and allowing it
 * would start a session the asker has already forgotten about.
 *
 * @returns {Function}
 */
export function cancelRequest() {
    return (dispatch, getState) => {
        const state = getState();
        const { requestedParticipant } = state['features/remote-control'].controller;
        const { conference } = state['features/base/conference'];
        if (!requestedParticipant) {
            return;
        }
        sendRemoteControlEndpointMessage(conference, requestedParticipant, {
            type: EVENTS.stop
        });
        dispatch(clearRequest());
        dispatch(setRemoteControlActive(false));
    };
}
/**
 * Handles permission request replies on the controller side.
 *
 * @param {string} participantId - The participant that sent the request.
 * @param {EndpointMessage} event - The permission request event.
 * @returns {Function}
 */
export function processPermissionRequestReply(participantId, event) {
    return (dispatch, getState) => {
        const state = getState();
        const { action, name, type } = event;
        const { requestedParticipant } = state['features/remote-control'].controller;
        if (isRemoteControlEnabled(state) && name === REMOTE_CONTROL_MESSAGE_NAME && type === EVENTS.permissions
            && participantId === requestedParticipant) {
            let descriptionKey, permissionGranted = false;
            switch (action) {
                case PERMISSIONS_ACTIONS.grant: {
                    dispatch({
                        type: SET_CONTROLLED_PARTICIPANT,
                        controlled: participantId
                    });
                    logger.log('Remote control permissions granted!', participantId);
                    logger.log('Starting remote control controller.');
                    const { conference } = state['features/base/conference'];
                    stopListener = (participant, stopEvent) => {
                        dispatch(handleRemoteControlStoppedEvent(participant.getId(), stopEvent));
                    };
                    conference?.on(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, stopListener);
                    dispatch(resume());
                    permissionGranted = true;
                    descriptionKey = 'dialog.remoteControlAllowedMessage';
                    break;
                }
                case PERMISSIONS_ACTIONS.deny:
                    logger.log('Remote control permissions denied!', participantId);
                    descriptionKey = 'dialog.remoteControlDeniedMessage';
                    break;
                case PERMISSIONS_ACTIONS.unavailable:
                    logger.log('Remote control is not set up on that machine', participantId);
                    descriptionKey = 'dialog.remoteControlUnavailableMessage';
                    break;
                case PERMISSIONS_ACTIONS.error:
                    logger.error('Error occurred on receiver side');
                    descriptionKey = 'dialog.remoteControlErrorMessage';
                    break;
                default:
                    logger.error('Unknown reply received!');
                    descriptionKey = 'dialog.remoteControlErrorMessage';
            }
            dispatch(clearRequest());
            if (!permissionGranted) {
                dispatch(setRemoteControlActive(false));
            }
            dispatch(showNotification({
                descriptionArguments: { user: getParticipantDisplayName(state, participantId) },
                descriptionKey,
                titleKey: 'dialog.remoteControlTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            if (permissionGranted) {
                // the remote control permissions has been granted
                // pin the controlled participant
                const pinnedParticipant = getPinnedParticipant(state);
                const virtualScreenshareParticipant = getVirtualScreenshareParticipantByOwnerId(state, participantId);
                const pinnedId = pinnedParticipant?.id;
                if (virtualScreenshareParticipant?.id && pinnedId !== virtualScreenshareParticipant?.id) {
                    dispatch(pinParticipant(virtualScreenshareParticipant?.id));
                }
                else if (!virtualScreenshareParticipant?.id && pinnedId !== participantId) {
                    dispatch(pinParticipant(participantId));
                }
            }
        }
        else {
            // different message type or another user -> ignoring the message
        }
    };
}
/**
 * Handles remote control stopped.
 *
 * @param {string} participantId - The ID of the participant that has sent the event.
 * @param {EndpointMessage} event - EndpointMessage event from the data channels.
 * @property {string} type - The function process only events with name REMOTE_CONTROL_MESSAGE_NAME.
 * @returns {void}
 */
export function handleRemoteControlStoppedEvent(participantId, event) {
    return (dispatch, getState) => {
        const state = getState();
        const { name, type } = event;
        const { controlled } = state['features/remote-control'].controller;
        if (isRemoteControlEnabled(state) && name === REMOTE_CONTROL_MESSAGE_NAME && type === EVENTS.stop
            && participantId === controlled) {
            dispatch(stopController());
        }
    };
}
/**
 * Stops processing the mouse and keyboard events. Removes added listeners.
 * Enables the keyboard shortcuts. Displays dialog to notify the user that remote control session has ended.
 *
 * @param {boolean} notifyRemoteParty - If true a endpoint message to the controlled participant will be sent.
 * @returns {void}
 */
export function stopController(notifyRemoteParty = false) {
    return (dispatch, getState) => {
        const state = getState();
        const { controlled } = state['features/remote-control'].controller;
        if (!controlled) {
            return;
        }
        const { conference } = state['features/base/conference'];
        if (notifyRemoteParty) {
            sendRemoteControlEndpointMessage(conference, controlled, {
                type: EVENTS.stop
            });
        }
        logger.log('Stopping remote control controller.');
        conference?.off(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, stopListener);
        stopListener = undefined;
        dispatch(pause());
        dispatch({
            type: SET_CONTROLLED_PARTICIPANT,
            controlled: undefined
        });
        dispatch(setRemoteControlActive(false));
        dispatch(showNotification({
            descriptionKey: 'dialog.remoteControlStopMessage',
            titleKey: 'dialog.remoteControlTitle'
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    };
}
/**
 * Clears a pending permission request.
 *
 * @returns {Function}
 */
export function clearRequest() {
    return (dispatch, getState) => {
        const { conference } = getState()['features/base/conference'];
        dispatch({
            type: SET_REQUESTED_PARTICIPANT,
            requestedParticipant: undefined
        });
        conference?.off(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, permissionsReplyListener);
        permissionsReplyListener = undefined;
    };
}
/**
 * Sets that transport object that is used by the receiver to communicate with the native part of the remote control
 * implementation.
 *
 * @param {Transport} transport - The transport to be set.
 * @returns {{
 *      type: SET_RECEIVER_TRANSPORT,
 *      transport: Transport
 * }}
 */
export function setReceiverTransport(transport) {
    return {
        type: SET_RECEIVER_TRANSPORT,
        transport
    };
}
/**
 * Enables the receiver functionality.
 *
 * @returns {Function}
 */
export function enableReceiver() {
    return (dispatch, getState) => {
        const state = getState();
        const { enabled } = state['features/remote-control'].receiver;
        if (enabled) {
            return;
        }
        const { connection } = state['features/base/connection'];
        const { conference } = state['features/base/conference'];
        if (!connection || !conference) {
            logger.error('Couldn\'t enable the remote receiver! The connection or conference instance is undefined!');
            return;
        }
        dispatch({
            type: SET_RECEIVER_ENABLED,
            enabled: true
        });
        connection.addFeature(DISCO_REMOTE_CONTROL_FEATURE, true);
        receiverEndpointMessageListener = (participant, message) => {
            dispatch(endpointMessageReceived(participant.getId(), message));
        };
        conference.on(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, receiverEndpointMessageListener);
    };
}
/**
 * Disables the receiver functionality.
 *
 * @returns {Function}
 */
export function disableReceiver() {
    return (dispatch, getState) => {
        const state = getState();
        const { enabled } = state['features/remote-control'].receiver;
        if (!enabled) {
            return;
        }
        const { connection } = state['features/base/connection'];
        const { conference } = state['features/base/conference'];
        if (!connection || !conference) {
            logger.error('Couldn\'t enable the remote receiver! The connection or conference instance is undefined!');
            return;
        }
        logger.log('Remote control receiver disabled.');
        dispatch({
            type: SET_RECEIVER_ENABLED,
            enabled: false
        });
        dispatch(stopReceiver(true));
        connection.removeFeature(DISCO_REMOTE_CONTROL_FEATURE);
        conference.off(JitsiConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, receiverEndpointMessageListener);
    };
}
/**
 * Stops a remote control session on the receiver side.
 *
 * @param {boolean} [dontNotifyLocalParty] - If true - a notification about stopping
 * the remote control won't be displayed.
 * @param {boolean} [dontNotifyRemoteParty] - If true a endpoint message to the controller participant will be sent.
 * @returns {Function}
 */
export function stopReceiver(dontNotifyLocalParty = false, dontNotifyRemoteParty = false) {
    return (dispatch, getState) => {
        const state = getState();
        const { receiver } = state['features/remote-control'];
        const { controller, transport } = receiver;
        if (!controller) {
            return;
        }
        const { conference } = state['features/base/conference'];
        if (!dontNotifyRemoteParty) {
            sendRemoteControlEndpointMessage(conference, controller, {
                type: EVENTS.stop
            });
        }
        dispatch({
            type: SET_CONTROLLER,
            controller: undefined
        });
        transport?.sendEvent({
            name: REMOTE_CONTROL_MESSAGE_NAME,
            type: EVENTS.stop
        });
        dispatch(setRemoteControlActive(false));
        if (!dontNotifyLocalParty) {
            dispatch(showNotification({
                descriptionKey: 'dialog.remoteControlStopMessage',
                titleKey: 'dialog.remoteControlTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    };
}
/**
 * Handles only remote control endpoint messages.
 *
 * @param {string} participantId - The controller participant ID.
 * @param {Object} message - EndpointMessage from the data channels.
 * @param {string} message.name - The function processes only messages with
 * name REMOTE_CONTROL_MESSAGE_NAME.
 * @returns {Function}
 */
export function endpointMessageReceived(participantId, message) {
    return (dispatch, getState) => {
        const { action, name, type } = message;
        if (name !== REMOTE_CONTROL_MESSAGE_NAME) {
            return;
        }
        const state = getState();
        const { receiver } = state['features/remote-control'];
        const { enabled, transport } = receiver;
        if (enabled) {
            const { controller } = receiver;
            if (!controller && type === EVENTS.permissions && action === PERMISSIONS_ACTIONS.request) {
                dispatch(setRemoteControlActive(true));
                dispatch(openRemoteControlAuthorizationDialog(participantId));
            }
            else if (!controller && type === EVENTS.stop) {
                // They withdrew the request before it was answered. Take the
                // question away rather than leaving somebody deciding about
                // something that is no longer being asked.
                dispatch(hideDialog(undefined, RemoteControlAuthorizationDialog));
                dispatch(setRemoteControlActive(false));
            }
            else if (controller === participantId) {
                if (type === EVENTS.stop) {
                    dispatch(stopReceiver(false, true));
                }
                else { // forward the message
                    try {
                        transport?.sendEvent(message);
                    }
                    catch (error) {
                        logger.error('Error while trying to execute remote control message', error);
                    }
                }
            } // else ignore
        }
        else {
            logger.log('Remote control message is ignored because remote control is disabled', message);
        }
    };
}
/**
 * Tells the asker that this machine cannot hand over control yet, because the
 * helper that would do it is not installed or not usable.
 *
 * Answering with a plain refusal would be untrue and would discourage them from
 * asking again once it is sorted out.
 *
 * @param {string} participantId - The id of the participant who asked.
 * @returns {Function}
 */
export function declineUnavailable(participantId) {
    return (dispatch, getState) => {
        const { conference } = getState()['features/base/conference'];
        dispatch(setRemoteControlActive(false));
        sendRemoteControlEndpointMessage(conference, participantId, {
            type: EVENTS.permissions,
            action: PERMISSIONS_ACTIONS.unavailable
        });
    };
}
/**
 * Denies remote control access for user associated with the passed user id.
 *
 * @param {string} participantId - The id associated with the user who sent the
 * request for remote control authorization.
 * @returns {Function}
 */
export function deny(participantId) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        dispatch(setRemoteControlActive(false));
        sendRemoteControlEndpointMessage(conference, participantId, {
            type: EVENTS.permissions,
            action: PERMISSIONS_ACTIONS.deny
        });
    };
}
/**
 * Starts the session on the local machine's control agent.
 *
 * Upstream sends the Electron desktopCapturer's `sourceId` here. A browser has
 * no such thing — `getDisplayMedia` deliberately will not say which screen it
 * captured — so `sourceId` is always undefined and this rejected every time,
 * which is the immediate reason granting control has never worked outside
 * Electron. The browser path sends the display the page worked out instead.
 *
 * @returns {Function}
 */
export function sendStartRequest() {
    return (dispatch, getState) => {
        const state = getState();
        const { connection } = state['features/remote-control'].agent;
        const { transport } = state['features/remote-control'].receiver;
        const tracks = state['features/base/tracks'];
        const track = getLocalDesktopTrack(tracks);
        if (connection) {
            if (!isWholeScreenShared(state)) {
                return Promise.reject(new Error('Remote control needs a whole screen to be shared. A single window or '
                    + 'browser tab cannot be mapped to positions on the desktop.'));
            }
            return startAgentSession(connection, findSharedDisplay(state), getSharedDisplaySize(state));
        }
        // A host application, such as an Electron shell, drives the session by
        // the capture source id instead.
        const { sourceId } = track?.jitsiTrack || {};
        if (typeof sourceId === 'undefined') {
            return Promise.reject(new Error('Cannot identify screen for the remote control session'));
        }
        return transport?.sendRequest({
            name: REMOTE_CONTROL_MESSAGE_NAME,
            type: REQUESTS.start,
            sourceId
        });
    };
}
/**
 * Grants remote control access to user associated with the passed user id.
 *
 * @param {string} participantId - The id associated with the user who sent the
 * request for remote control authorization.
 * @returns {Function}
 */
export function grant(participantId) {
    return async (dispatch, getState) => {
        dispatch({
            type: SET_CONTROLLER,
            controller: participantId
        });
        logger.log(`Remote control permissions granted to: ${participantId}`);
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { connection } = state['features/remote-control'].agent;
        try {
            // The first time this site is used for control on this computer,
            // the agent asks the person sitting at it to approve the site —
            // through a dialog on their own desktop that no web page can raise
            // or answer. Doing it here, rather than on joining, means the
            // question arrives at the moment it makes sense: they have just
            // said yes to a specific person.
            if (connection && !connection.hello.paired) {
                await connection.pair();
            }
            if (!isScreenVideoShared(getState())) {
                await dispatch(toggleScreensharing(true, false, { desktopSharingSources: ['screen'] }));
            }
            await dispatch(sendStartRequest());
            sendRemoteControlEndpointMessage(conference, participantId, {
                type: EVENTS.permissions,
                action: PERMISSIONS_ACTIONS.grant
            });
        }
        catch (error) {
            logger.error(error);
            sendRemoteControlEndpointMessage(conference, participantId, {
                type: EVENTS.permissions,
                action: PERMISSIONS_ACTIONS.error
            });
            dispatch(showNotification({
                description: error?.message,
                descriptionKey: error?.message ? undefined : 'dialog.startRemoteControlErrorMessage',
                titleKey: 'dialog.remoteControlTitle'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            dispatch(stopReceiver(true));
        }
    };
}
/**
 * Handler for mouse button events on the controller side.
 *
 * @param {string} type - The type of event ("mousedown"/"mouseup").
 * @param {number} button - 1 (left), 2 (middle) or 3 (right).
 * @returns {Function}
 */
export function mouseClicked(type, button) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { controller } = state['features/remote-control'];
        sendRemoteControlEndpointMessage(conference, controller.controlled, {
            type,
            button
        });
    };
}
/**
 * Handles mouse movement on the controller side.
 *
 * @param {number} x - The pointer's horizontal position as a fraction of the
 * shared screen.
 * @param {number} y - The pointer's vertical position as a fraction of the
 * shared screen.
 * @returns {Function}
 */
export function mouseMoved(x, y) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { controller } = state['features/remote-control'];
        sendRemoteControlEndpointMessage(conference, controller.controlled, {
            type: EVENTS.mousemove,
            x,
            y
        });
    };
}
/**
 * Handles mouse scroll events on the controller side.
 *
 * @param {number} deltaX - The horizontal scroll amount in pixels.
 * @param {number} deltaY - The vertical scroll amount in pixels.
 * @returns {Function}
 */
export function mouseScrolled(deltaX, deltaY) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { controller } = state['features/remote-control'];
        sendRemoteControlEndpointMessage(conference, controller.controlled, {
            type: EVENTS.mousescroll,
            x: deltaX,
            y: deltaY
        });
    };
}
/**
 * Handles key events on the controller side.
 *
 * @param {string} type - The type of event ("keydown"/"keyup").
 * @param {string} key - The physical key, from the KEYS vocabulary.
 * @param {string[]} modifiers - The modifiers held at the time.
 * @returns {Function}
 */
export function keyPressed(type, key, modifiers) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { controller } = state['features/remote-control'];
        sendRemoteControlEndpointMessage(conference, controller.controlled, {
            type,
            key,
            modifiers
        });
    };
}
/**
 * Starts collecting remote control events and disables the app's own keyboard
 * shortcuts, so that typing goes to the remote machine rather than muting the
 * local microphone.
 *
 * @returns {Function}
 */
export function resume() {
    return (dispatch, getState) => {
        const state = getState();
        const { controlled, isCapturingEvents } = state['features/remote-control'].controller;
        if (!isRemoteControlEnabled(state) || !controlled || isCapturingEvents) {
            return;
        }
        logger.log('Resuming remote control controller.');
        dispatch({
            type: CAPTURE_EVENTS,
            isCapturingEvents: true
        });
    };
}
/**
 * Stops collecting events and gives the keyboard shortcuts back. The session
 * stays open; it can be picked up again with {@link resume}.
 *
 * @returns {Function}
 */
export function pause() {
    return (dispatch, getState) => {
        const state = getState();
        const { controlled, isCapturingEvents } = state['features/remote-control'].controller;
        if (!isRemoteControlEnabled(state) || !controlled || !isCapturingEvents) {
            return;
        }
        logger.log('Pausing remote control controller.');
        dispatch(setControllerEngaged(false));
        dispatch({
            type: CAPTURE_EVENTS,
            isCapturingEvents: false
        });
    };
}
/**
 * Takes hold of the remote machine, or lets go of it.
 *
 * Having permission and actually driving are deliberately separate. A
 * controller has to click into the shared screen before their keystrokes leave
 * their own machine, and Escape hands the keyboard straight back — so a stray
 * password typed into the wrong window never reaches someone else's computer.
 *
 * @param {boolean} engaged - Whether the controller is driving.
 * @returns {Object}
 */
export function setControllerEngaged(engaged) {
    return {
        type: SET_CONTROLLER_ENGAGED,
        engaged
    };
}
/**
 * Reports whether this machine could run the control helper at all.
 *
 * A phone cannot, and neither can a browser with no screen sharing, so neither
 * should offer to be controlled.
 *
 * @returns {boolean}
 */
function canRunAgent() {
    return !isMobileBrowser() && JitsiMeetJS.isDesktopSharingEnabled();
}
/**
 * Records which display the local participant says they are sharing.
 *
 * @param {string} displayId - The display's id, as the agent reported it.
 * @returns {Object}
 */
export function setChosenDisplay(displayId) {
    return {
        type: SET_CHOSEN_DISPLAY,
        displayId
    };
}
/**
 * Looks for the control agent on this machine and records what it found.
 *
 * Nothing here asks anybody for anything: discovery is silent, and the result
 * only decides whether this participant can be *asked* for control.
 *
 * @returns {Function}
 */
export function discoverAgent() {
    return async (dispatch, getState) => {
        if (!isRemoteControlEnabled(getState())) {
            return;
        }
        // Rejoining a conference dispatches this again; the previous socket
        // would otherwise be left open with its heartbeat still running.
        getState()['features/remote-control'].agent.connection?.close();
        let connection;
        try {
            connection = await connectToAgent();
        }
        catch (error) {
            logger.warn('Could not look for the remote control agent', error);
        }
        if (!connection) {
            dispatch({
                type: SET_AGENT_STATE,
                status: AgentStatus.Missing
            });
            // Being askable without the helper installed is deliberate. It is
            // the only way anyone finds out the helper exists: if this
            // participant advertised nothing, the menu item would never appear
            // for anybody, and the feature would be invisible to exactly the
            // people who need it. The request is answered with an explanation
            // and a download rather than silence.
            if (canRunAgent()) {
                dispatch(enableReceiver());
            }
            return;
        }
        const { hello } = connection;
        logger.log(`Remote control agent ${hello.version} on ${hello.os}/${hello.arch} `
            + `using ${hello.injector} (ready: ${hello.ready})`);
        dispatch({
            type: SET_AGENT_STATE,
            connection,
            hello,
            status: hello.ready ? AgentStatus.Available : AgentStatus.NotReady
        });
        // A helper that is installed but missing a permission still makes this
        // participant askable, so the request can explain what to fix rather
        // than the option quietly not being there.
        if (!hello.ready) {
            dispatch(enableReceiver());
        }
        // The agent becomes the transport the receiver drives. A host
        // application's transport is left in place when there is no agent, so
        // an Electron shell would still work.
        if (hello.ready) {
            dispatch(setReceiverTransport(connection.transport));
        }
    };
}
/**
 * Drops the connection to the local agent.
 *
 * @returns {Function}
 */
export function disconnectAgent() {
    return (dispatch, getState) => {
        const { connection } = getState()['features/remote-control'].agent;
        connection?.close();
        dispatch({
            type: SET_AGENT_STATE,
            status: AgentStatus.Missing
        });
    };
}
/**
 * Sends a key chord to the controlled machine as one atomic action.
 *
 * A chord assembled from separate keydown messages can come apart over a lossy
 * data channel and leave a modifier stuck down on someone else's keyboard, so
 * the whole chord travels as a single request instead.
 *
 * @param {string[]} modifiers - The modifiers to hold.
 * @param {string} key - The key to tap.
 * @returns {Function}
 */
export function sendControllerCombo(modifiers, key) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { controlled } = state['features/remote-control'].controller;
        if (!controlled) {
            return;
        }
        sendRemoteControlEndpointMessage(conference, controlled, {
            type: EVENTS.keycombo,
            key,
            modifiers
        });
    };
}
/**
 * Types text on the controlled machine.
 *
 * The characters are resolved to keys by the receiver's own agent rather than
 * here, because only that machine knows its keyboard layout.
 *
 * @param {string} text - The text to type.
 * @returns {Function}
 */
export function sendControllerText(text) {
    return (dispatch, getState) => {
        const state = getState();
        const { conference } = state['features/base/conference'];
        const { controlled } = state['features/remote-control'].controller;
        if (!controlled || !text) {
            return;
        }
        sendRemoteControlEndpointMessage(conference, controlled, {
            type: EVENTS.typetext,
            text
        });
    };
}
